// Succession — bilingual display labels. Client-facing UI never shows raw enums.
export const succLabel = (isES, kind, value) => {
  const M = {
    criticality: {
      high: ['Alta', 'High'], medium: ['Media', 'Medium'], low: ['Baja', 'Low'],
    },
    risk: {
      high: ['Alto', 'High'], medium: ['Medio', 'Medium'], low: ['Bajo', 'Low'],
    },
    horizon: {
      '0_1y': ['0–1 año', '0–1 year'], '1_2y': ['1–2 años', '1–2 years'],
      '2y_plus': ['2+ años', '2+ years'], undetermined: ['Por determinar', 'To be determined'],
    },
    coverage: {
      none: ['Sin sucesor', 'No successor'], long_term_only: ['Solo largo plazo', 'Long-term only'],
      near_ready: ['Sucesor cercano', 'Near-ready'], ready_now: ['Cobertura lista', 'Ready-now'],
    },
    plan: {
      no_successor: ['Requiere atención', 'Needs attention'], not_started: ['No iniciado', 'Not started'],
      in_development: ['En desarrollo', 'In development'], ready: ['Listo', 'Ready'],
    },
    readiness: {
      ready_now: ['Listo/a ahora', 'Ready now'], ready_1y: ['Listo/a en 1 año', 'Ready in 1 year'],
      ready_2y: ['Listo/a en 2+ años', 'Ready in 2+ years'],
      development_required: ['Requiere desarrollo', 'Development required'],
    },
    actionStatus: {
      planned: ['Planificada', 'Planned'], in_progress: ['En curso', 'In progress'],
      completed: ['Completada', 'Completed'], cancelled: ['Cancelada', 'Cancelled'],
    },
    rating: {
      meets: ['Cumple', 'Meets'], partial: ['Cumple parcialmente', 'Partially meets'],
      not_met: ['No cumple', 'Does not meet'], no_evidence: ['Sin evidencia', 'No evidence'],
    },
    importance: {
      essential: ['Esencial', 'Essential'], desirable: ['Deseable', 'Desirable'],
    },
    band: {
      insufficient: ['Evidencia insuficiente', 'Insufficient evidence'],
      partial: ['Evidencia parcial', 'Partial evidence'],
      strong: ['Evidencia suficiente', 'Sufficient evidence'],
    },
    quadrant: {
      technical: ['Nivel técnico ideal', 'Ideal technical level'],
      education: ['Nivel educativo ideal', 'Ideal education level'],
      experience: ['Experiencia ideal', 'Ideal experience'],
      critical_experiences: ['Experiencias críticas deseables', 'Desirable critical experiences'],
    },
    continuity: {
      incumbent_challenges: ['Retos enfrentados por el titular actual', 'Challenges faced by current incumbent'],
      incumbent_lessons: ['Lecciones del titular actual', 'Lessons from the current incumbent'],
      future_challenges: ['Retos futuros de la posición', 'Future challenges of the position'],
      critical_knowledge: ['Conocimiento crítico', 'Critical knowledge'],
      legal_regulatory: ['Marco legal y regulatorio', 'Legal and regulatory framework'],
      key_relationships: ['Relaciones clave', 'Key relationships'],
      critical_decisions: ['Decisiones y responsabilidades críticas', 'Critical decisions and responsibilities'],
      transition_risks: ['Riesgos de transición', 'Transition risks'],
    },
    activity: {
      succ_position_created: ['Posición crítica creada', 'Critical position created'],
      succ_position_updated: ['Posición actualizada', 'Position updated'],
      succ_criticality_changed: ['Criticidad actualizada', 'Criticality changed'],
      succ_risk_changed: ['Riesgo de sucesión actualizado', 'Succession risk changed'],
      succ_horizon_changed: ['Horizonte de sucesión actualizado', 'Succession horizon changed'],
      succ_incumbent_changed: ['Titular actualizado', 'Incumbent changed'],
      succ_position_deleted: ['Posición eliminada', 'Position deleted'],
      succ_successor_added: ['Sucesor potencial identificado', 'Potential successor identified'],
      succ_successor_removed: ['Sucesor potencial retirado', 'Potential successor removed'],
      succ_readiness_changed: ['Preparación actualizada', 'Readiness changed'],
      succ_readiness_reviewed: ['Preparación revisada', 'Readiness reviewed'],
      succ_review_schedule_changed: ['Calendario de revisión actualizado', 'Review schedule changed'],
      succ_continuity_changed: ['Perfil de continuidad actualizado', 'Continuity profile updated'],
      succ_continuity_generated: ['Borrador de continuidad generado', 'Continuity draft generated'],
      succ_ideal_changed: ['Escenario ideal actualizado', 'Ideal scenario updated'],
      succ_ideal_generated: ['Borrador de escenario ideal generado', 'Ideal-scenario draft generated'],
      succ_assessment_changed: ['Evaluación de requisitos actualizada', 'Requirement assessment updated'],
      succ_dev_action_created: ['Acción de desarrollo creada', 'Development action created'],
      succ_dev_action_updated: ['Acción de desarrollo actualizada', 'Development action updated'],
      succ_sent_to_talenttrain: ['Acción enviada a TalentTrain', 'Action sent to TalentTrain'],
      succ_plan_exported: ['Plan de sucesión exportado', 'Succession plan exported'],
    },
  };
  const e = (M[kind] || {})[value];
  return e ? e[isES ? 0 : 1] : (value || '');
};

// Human-readable audit line: "<action label>: <object> · before → after" (no raw enums, no IDs)
export const auditLine = (isES, ev) => {
  const label = succLabel(isES, 'activity', ev.action);
  const ex = ev.extra || {};
  const obj = ex.successor || ex.objective || (ex.section ? succLabel(isES, 'continuity', ex.section) : null)
    || (ex.quadrant ? succLabel(isES, 'quadrant', ex.quadrant) : null) || null;
  let line = obj ? `${label}: ${obj}` : label;
  const localize = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    for (const kind of ['readiness', 'criticality', 'risk', 'horizon']) {
      const l = succLabel(isES, kind, v);
      if (l !== v) return l;
    }
    return typeof v === 'object' ? Object.values(v).map(localize).join(', ') : String(v);
  };
  if (ex.before !== undefined && ex.after !== undefined) {
    line += ` · ${localize(ex.before)} → ${localize(ex.after)}`;
  }
  return line;
};

export const riskTone = {
  high: 'bg-destructive/10 text-destructive border-destructive/30',
  medium: 'bg-warning/10 text-warning border-warning/30',
  low: 'bg-success/10 text-success border-success/30',
};

export const planTone = {
  no_successor: 'bg-warning/10 text-warning border-warning/30',
  not_started: 'bg-muted text-muted-foreground border-border',
  in_development: 'bg-info/10 text-info border-info/30',
  ready: 'bg-success/10 text-success border-success/30',
};
